"use strict";
// shared-types/src/database/material-attribute-model.ts
//
// Attribute_Type_Model — the hand-written source of truth for the *values* a
// material may carry, typed per category and per subcategory.
//
// This model is kept in agreement with the runtime config (`CATEGORY_DEFINITIONS`
// in ./material-categories.ts) by a behavioral conformance test (see the
// material-custom-properties spec, Property 3). There is no codegen and no
// snapshot assertion: both the literal-union types AND the runtime `OPTION_UNIONS`
// companion below are derived from the same hand-authored `as const` option
// tuples, so the type side and the runtime side cannot drift from each other; the
// conformance test then checks that side against `CATEGORY_DEFINITIONS`.
//
// Mapping rule (data_type + options -> TypeScript), applied to every property:
//   - data_type: 'string'       WITH options  -> literal union of those options
//   - data_type: 'string_array' WITH options  -> Array<literal union of options>
//   - data_type: 'string'       WITHOUT options -> string
//   - data_type: 'number'                      -> number
//
// Rules:
//   - All properties are OPTIONAL (a material only carries fields for its
//     category/subcategory).
//   - Universal fields are EXCLUDED — they live on the base record types
//     (product_name, name, brand, description, sku_or_upc, quantity, unit,
//     storage_location, status, purchase_source, purchase_date, purchase_price,
//     notes).
//   - Each category has a `{Category}BaseAttributes` interface for its
//     category-level fields. Each subcategory that ADDS fields has a
//     `{Category}{Subcategory}Attributes` interface that `extends` the base.
//     Subcategories that add no fields simply reuse the base type.
//
// NOTE: `data_type: 'string_array'` is supported by the mapping rule (it maps to
// Array<union>) but no current field in CATEGORY_DEFINITIONS uses it.
Object.defineProperty(exports, "__esModule", { value: true });
exports.OPTION_UNIONS = exports.TOOLS_PRIMARY_MATERIAL_OPTIONS = exports.BLANKS_BASES_BLANK_TYPE_OPTIONS = exports.EMBELLISHMENTS_FINISH_OPTIONS = exports.STORAGE_PRIMARY_MATERIAL_OPTIONS = exports.KIDS_CRAFTS_AGE_RANGE_OPTIONS = exports.STUFFING_DOLLMAKING_DENSITY_OPTIONS = exports.STUFFING_DOLLMAKING_PRIMARY_MATERIAL_OPTIONS = exports.FLORAL_DECOR_ITEM_TYPE_OPTIONS = exports.WOOD_DIY_FINISH_OPTIONS = exports.WOOD_DIY_PRIMARY_MATERIAL_OPTIONS = exports.CLAY_RESIN_ITEM_TYPE_OPTIONS = exports.ADHESIVES_FORM_OPTIONS = exports.ADHESIVES_APPLICATION_OPTIONS = exports.ADHESIVES_ITEM_TYPE_OPTIONS = exports.CUTTING_MACHINES_CARRIER_OPTIONS = exports.CUTTING_MACHINES_ITEM_TYPE_OPTIONS = exports.BEADS_TEMPER_OPTIONS = exports.BEADS_SHAPE_OPTIONS = exports.BEADS_FINISH_OPTIONS = exports.BEADS_PRIMARY_MATERIAL_OPTIONS = exports.ART_SUPPLIES_SURFACE_OPTIONS = exports.ART_SUPPLIES_FINISH_OPTIONS = exports.ART_SUPPLIES_MEDIUM_OPTIONS = exports.PAPER_FINISH_OPTIONS = exports.NEEDLE_ARTS_ITEM_TYPE_OPTIONS = exports.NEEDLES_HOOKS_PRIMARY_MATERIAL_OPTIONS = exports.YARN_SKEIN_WEIGHT_UNIT_OPTIONS = exports.YARN_LENGTH_UNIT_OPTIONS = exports.YARN_FIBER_FAMILY_OPTIONS = exports.YARN_WEIGHT_OPTIONS = exports.SEWING_NOTIONS_LOFT_OPTIONS = exports.SEWING_NOTIONS_STABILIZER_TYPE_OPTIONS = exports.SEWING_NOTIONS_INTERFACING_WEIGHT_OPTIONS = exports.SEWING_NOTIONS_ZIPPER_TYPE_OPTIONS = exports.SEWING_NOTIONS_NEEDLE_TYPE_OPTIONS = exports.SEWING_NOTIONS_THREAD_TYPE_OPTIONS = exports.FABRIC_PATTERN_SCALE_OPTIONS = exports.FABRIC_STRETCH_OPTIONS = exports.FABRIC_WEIGHT_OPTIONS = exports.FABRIC_WIDTH_UNIT_OPTIONS = exports.FABRIC_FABRIC_TYPE_OPTIONS = exports.CROCHET_HOOK_SIZE_OPTIONS = exports.KNITTING_NEEDLE_SIZE_OPTIONS = exports.COLOR_FAMILY_OPTIONS = void 0;
// ---------------------------------------------------------------------------
// Shared option tuples + derived literal-union type aliases
// ---------------------------------------------------------------------------
/** Shared color families (from getColorFields / COLOR_FAMILIES) */
exports.COLOR_FAMILY_OPTIONS = [
    'black', 'white', 'gray', 'brown', 'red', 'orange', 'yellow',
    'green', 'blue', 'purple', 'pink', 'metallic', 'multicolor', 'clear', 'natural',
];
// --- Needle / hook size option tuples (toSizeOptions output) ---
/** Knitting needle sizes — toSizeOptions(KNITTING_NEEDLE_SIZES) */
exports.KNITTING_NEEDLE_SIZE_OPTIONS = [
    '1.50 mm (US 000)', '1.75 mm (US 00)', '2.00 mm (US 0)', '2.25 mm (US 1)',
    '2.75 mm (US 2)', '3.00 mm', '3.125 mm (US 3)', '3.25 mm (US 3)',
    '3.50 mm (US 4)', '3.75 mm (US 5)', '4.00 mm (US 6)', '4.25 mm (US 6)',
    '4.50 mm (US 7)', '5.00 mm (US 8)', '5.25 mm (US 9)', '5.50 mm (US 9)',
    '5.75 mm (US 10)', '6.00 mm (US 10)', '6.50 mm (US 10½)', '7.00 mm',
    '8.00 mm (US 11)', '9.00 mm (US 13)', '10.00 mm (US 15)', '12.50 mm (US 17)',
    '12.75 mm (US 17)', '15.00 mm (US 19)', '19.00 mm (US 35)', '25.00 mm (US 50)',
    '35.00 mm (US 70)',
];
/** Crochet hook sizes — toSizeOptions(CROCHET_HOOK_SIZES) */
exports.CROCHET_HOOK_SIZE_OPTIONS = [
    '2.25 mm (B-1)', '2.50 mm', '2.75 mm (C-2)', '3.125 mm (D)',
    '3.25 mm (D-3)', '3.50 mm (E-4)', '3.75 mm (F-5)', '4.00 mm (G-6)',
    '4.25 mm (G)', '4.50 mm (7)', '5.00 mm (H-8)', '5.25 mm (I)',
    '5.50 mm (I-9)', '5.75 mm (J)', '6.00 mm (J-10)', '6.50 mm (K-10½)',
    '7.00 mm', '8.00 mm (L-11)', '9.00 mm (M/N-13)', '10.00 mm (N/P-15)',
    '11.50 mm (P-16)', '12.00 mm', '15.00 mm (P/Q)', '15.75 mm (Q)',
    '16.00 mm (Q)', '19.00 mm (S)', '25.00 mm (T/U/X)', '30.00 mm (T/X)',
];
// --- Per-category option tuples ---
// 1) fabric
exports.FABRIC_FABRIC_TYPE_OPTIONS = ['woven', 'knit', 'nonwoven', 'leather', 'vinyl'];
exports.FABRIC_WIDTH_UNIT_OPTIONS = ['inches', 'cm'];
exports.FABRIC_WEIGHT_OPTIONS = ['lightweight', 'medium', 'heavyweight'];
exports.FABRIC_STRETCH_OPTIONS = ['none', 'slight', 'moderate', 'high'];
exports.FABRIC_PATTERN_SCALE_OPTIONS = ['solid', 'small', 'medium', 'large'];
// 2) sewing_notions
exports.SEWING_NOTIONS_THREAD_TYPE_OPTIONS = ['all_purpose', 'quilting', 'embroidery', 'topstitch', 'serger'];
exports.SEWING_NOTIONS_NEEDLE_TYPE_OPTIONS = ['sharps', 'embroidery', 'quilting', 'darning', 'leather'];
exports.SEWING_NOTIONS_ZIPPER_TYPE_OPTIONS = ['invisible', 'separating', 'coil', 'metal'];
exports.SEWING_NOTIONS_INTERFACING_WEIGHT_OPTIONS = ['lightweight', 'medium', 'heavyweight'];
exports.SEWING_NOTIONS_STABILIZER_TYPE_OPTIONS = ['cut_away', 'tear_away', 'wash_away', 'heat_away'];
exports.SEWING_NOTIONS_LOFT_OPTIONS = ['low', 'medium', 'high'];
// 3) yarn
exports.YARN_WEIGHT_OPTIONS = ['0 - Lace', '1 - Super Fine', '2 - Fine', '3 - Light', '4 - Medium', '5 - Bulky', '6 - Super Bulky', '7 - Jumbo'];
exports.YARN_FIBER_FAMILY_OPTIONS = ['acrylic', 'cotton', 'wool', 'alpaca', 'silk', 'linen', 'bamboo', 'nylon', 'polyester', 'blend'];
exports.YARN_LENGTH_UNIT_OPTIONS = ['yards', 'meters'];
exports.YARN_SKEIN_WEIGHT_UNIT_OPTIONS = ['oz', 'g'];
// 4) needles_hooks
exports.NEEDLES_HOOKS_PRIMARY_MATERIAL_OPTIONS = ['metal', 'bamboo', 'wood', 'plastic', 'carbon_fiber', 'ergonomic'];
// 5) needle_arts
exports.NEEDLE_ARTS_ITEM_TYPE_OPTIONS = ['floss', 'thread', 'fabric', 'canvas', 'hoop', 'frame', 'kit'];
// 6) paper
exports.PAPER_FINISH_OPTIONS = ['matte', 'glossy', 'satin', 'textured', 'smooth'];
// 7) art_supplies
exports.ART_SUPPLIES_MEDIUM_OPTIONS = ['acrylic', 'watercolor', 'gouache', 'oil', 'ink', 'marker', 'pencil', 'pastel', 'spray'];
exports.ART_SUPPLIES_FINISH_OPTIONS = ['matte', 'satin', 'gloss'];
exports.ART_SUPPLIES_SURFACE_OPTIONS = ['paper', 'canvas', 'fabric', 'wood', 'multi'];
// 8) beads
exports.BEADS_PRIMARY_MATERIAL_OPTIONS = ['glass', 'crystal', 'metal', 'acrylic', 'wood', 'stone'];
exports.BEADS_FINISH_OPTIONS = ['matte', 'glossy', 'metallic', 'transparent', 'opaque', 'AB'];
exports.BEADS_SHAPE_OPTIONS = ['round', 'bicone', 'seed', 'bugle', 'cube', 'oval', 'teardrop'];
exports.BEADS_TEMPER_OPTIONS = ['dead_soft', 'half_hard', 'hard'];
// 9) cutting_machines
exports.CUTTING_MACHINES_ITEM_TYPE_OPTIONS = ['adhesive_vinyl', 'htv', 'transfer_tape', 'mat', 'blade', 'tool', 'accessory'];
exports.CUTTING_MACHINES_CARRIER_OPTIONS = ['warm_peel', 'cold_peel'];
// 10) adhesives
exports.ADHESIVES_ITEM_TYPE_OPTIONS = ['permanent', 'removable', 'repositionable', 'washable'];
exports.ADHESIVES_APPLICATION_OPTIONS = ['paper', 'fabric', 'vinyl', 'wood', 'multi'];
exports.ADHESIVES_FORM_OPTIONS = ['liquid', 'stick', 'tape', 'spray', 'hot_glue'];
// 11) clay_resin
exports.CLAY_RESIN_ITEM_TYPE_OPTIONS = ['polymer', 'air_dry', 'ceramic', 'epoxy_resin', 'uv_resin'];
// 12) wood_diy
exports.WOOD_DIY_PRIMARY_MATERIAL_OPTIONS = ['pine', 'oak', 'birch', 'mdf', 'plywood', 'balsa'];
exports.WOOD_DIY_FINISH_OPTIONS = ['unfinished', 'sanded', 'stained', 'painted'];
// 13) floral_decor
exports.FLORAL_DECOR_ITEM_TYPE_OPTIONS = ['artificial', 'dried', 'foam', 'wire', 'decor'];
// 14) stuffing_dollmaking
exports.STUFFING_DOLLMAKING_PRIMARY_MATERIAL_OPTIONS = ['polyester', 'cotton', 'wool', 'kapok', 'foam', 'pellets'];
exports.STUFFING_DOLLMAKING_DENSITY_OPTIONS = ['light', 'medium', 'firm'];
// 15) kids_crafts
exports.KIDS_CRAFTS_AGE_RANGE_OPTIONS = ['3+', '5+', '8+', '12+', 'adult'];
// 16) storage
exports.STORAGE_PRIMARY_MATERIAL_OPTIONS = ['plastic', 'fabric', 'cardboard', 'metal', 'wood'];
// 17) embellishments
exports.EMBELLISHMENTS_FINISH_OPTIONS = ['matte', 'glossy', 'metallic', 'iridescent'];
// 18) blanks_bases
exports.BLANKS_BASES_BLANK_TYPE_OPTIONS = ['tshirt', 'tote', 'hat', 'mug', 'tumbler', 'pillow_cover', 'sign', 'ornament', 'canvas_blank'];
// 19) tools
exports.TOOLS_PRIMARY_MATERIAL_OPTIONS = ['metal', 'plastic', 'bamboo', 'wood', 'carbon_fiber'];
// ---------------------------------------------------------------------------
// OPTION_UNIONS — runtime companion for the behavioral options-conformance check
// ---------------------------------------------------------------------------
// Literal unions erase at runtime, so the conformance test (Property 3, spec task
// 1.6) cannot read the union members reflectively. This `const` map is the real
// runtime data the test compares against each option-bearing field's `options`
// array in CATEGORY_DEFINITIONS, in both directions.
//
// Keys:
//   - Category-level option fields:        `<category>.<field>`
//   - Subcategory-added option fields:     `<category>.<subcategory>.<field>`
//     (subcategory-qualified because the same field name — e.g. `size_mm` on
//     needles_hooks — resolves to different option sets per subcategory).
//
// Each value references the same `as const` tuple that drives the corresponding
// literal-union type above, so the type side and runtime side cannot drift.
// The conformance test (task 1.6) resolves a field via
// getCategoryFields(category, subcategory): it looks up
// `<category>.<subcategory>.<field>` first, then falls back to `<category>.<field>`.
exports.OPTION_UNIONS = {
    // 1) fabric
    'fabric.fabric_type': exports.FABRIC_FABRIC_TYPE_OPTIONS,
    'fabric.width_unit': exports.FABRIC_WIDTH_UNIT_OPTIONS,
    'fabric.weight': exports.FABRIC_WEIGHT_OPTIONS,
    'fabric.stretch': exports.FABRIC_STRETCH_OPTIONS,
    'fabric.pattern_scale': exports.FABRIC_PATTERN_SCALE_OPTIONS,
    'fabric.color_family': exports.COLOR_FAMILY_OPTIONS,
    // 2) sewing_notions
    'sewing_notions.color_family': exports.COLOR_FAMILY_OPTIONS,
    'sewing_notions.thread.thread_type': exports.SEWING_NOTIONS_THREAD_TYPE_OPTIONS,
    'sewing_notions.needles_hand.needle_type': exports.SEWING_NOTIONS_NEEDLE_TYPE_OPTIONS,
    'sewing_notions.zippers.zipper_type': exports.SEWING_NOTIONS_ZIPPER_TYPE_OPTIONS,
    'sewing_notions.interfacing.interfacing_weight': exports.SEWING_NOTIONS_INTERFACING_WEIGHT_OPTIONS,
    'sewing_notions.stabilizer.stabilizer_type': exports.SEWING_NOTIONS_STABILIZER_TYPE_OPTIONS,
    'sewing_notions.batting.loft': exports.SEWING_NOTIONS_LOFT_OPTIONS,
    // 3) yarn
    'yarn.weight': exports.YARN_WEIGHT_OPTIONS,
    'yarn.fiber_family': exports.YARN_FIBER_FAMILY_OPTIONS,
    'yarn.length_unit': exports.YARN_LENGTH_UNIT_OPTIONS,
    'yarn.skein_weight_unit': exports.YARN_SKEIN_WEIGHT_UNIT_OPTIONS,
    'yarn.color_family': exports.COLOR_FAMILY_OPTIONS,
    // 4) needles_hooks
    'needles_hooks.primary_material': exports.NEEDLES_HOOKS_PRIMARY_MATERIAL_OPTIONS,
    'needles_hooks.knitting_straight.size_mm': exports.KNITTING_NEEDLE_SIZE_OPTIONS,
    'needles_hooks.knitting_circular.size_mm': exports.KNITTING_NEEDLE_SIZE_OPTIONS,
    'needles_hooks.knitting_dpn.size_mm': exports.KNITTING_NEEDLE_SIZE_OPTIONS,
    'needles_hooks.knitting_interchangeable.size_mm': exports.KNITTING_NEEDLE_SIZE_OPTIONS,
    'needles_hooks.crochet_hooks.size_mm': exports.CROCHET_HOOK_SIZE_OPTIONS,
    'needles_hooks.tunisian_hooks.size_mm': exports.CROCHET_HOOK_SIZE_OPTIONS,
    // 5) needle_arts
    'needle_arts.item_type': exports.NEEDLE_ARTS_ITEM_TYPE_OPTIONS,
    'needle_arts.color_family': exports.COLOR_FAMILY_OPTIONS,
    // 6) paper
    'paper.finish': exports.PAPER_FINISH_OPTIONS,
    'paper.color_family': exports.COLOR_FAMILY_OPTIONS,
    // 7) art_supplies
    'art_supplies.medium': exports.ART_SUPPLIES_MEDIUM_OPTIONS,
    'art_supplies.finish': exports.ART_SUPPLIES_FINISH_OPTIONS,
    'art_supplies.surface': exports.ART_SUPPLIES_SURFACE_OPTIONS,
    'art_supplies.color_family': exports.COLOR_FAMILY_OPTIONS,
    // 8) beads
    'beads.primary_material': exports.BEADS_PRIMARY_MATERIAL_OPTIONS,
    'beads.finish': exports.BEADS_FINISH_OPTIONS,
    'beads.shape': exports.BEADS_SHAPE_OPTIONS,
    'beads.color_family': exports.COLOR_FAMILY_OPTIONS,
    'beads.wire.temper': exports.BEADS_TEMPER_OPTIONS,
    // 9) cutting_machines
    'cutting_machines.item_type': exports.CUTTING_MACHINES_ITEM_TYPE_OPTIONS,
    'cutting_machines.color_family': exports.COLOR_FAMILY_OPTIONS,
    'cutting_machines.vinyl_htv.carrier': exports.CUTTING_MACHINES_CARRIER_OPTIONS,
    // 10) adhesives
    'adhesives.item_type': exports.ADHESIVES_ITEM_TYPE_OPTIONS,
    'adhesives.application': exports.ADHESIVES_APPLICATION_OPTIONS,
    'adhesives.form': exports.ADHESIVES_FORM_OPTIONS,
    // 11) clay_resin
    'clay_resin.item_type': exports.CLAY_RESIN_ITEM_TYPE_OPTIONS,
    'clay_resin.color_family': exports.COLOR_FAMILY_OPTIONS,
    // 12) wood_diy
    'wood_diy.primary_material': exports.WOOD_DIY_PRIMARY_MATERIAL_OPTIONS,
    'wood_diy.finish': exports.WOOD_DIY_FINISH_OPTIONS,
    // 13) floral_decor
    'floral_decor.item_type': exports.FLORAL_DECOR_ITEM_TYPE_OPTIONS,
    'floral_decor.color_family': exports.COLOR_FAMILY_OPTIONS,
    // 14) stuffing_dollmaking
    'stuffing_dollmaking.primary_material': exports.STUFFING_DOLLMAKING_PRIMARY_MATERIAL_OPTIONS,
    'stuffing_dollmaking.density': exports.STUFFING_DOLLMAKING_DENSITY_OPTIONS,
    // 15) kids_crafts
    'kids_crafts.age_range': exports.KIDS_CRAFTS_AGE_RANGE_OPTIONS,
    'kids_crafts.color_family': exports.COLOR_FAMILY_OPTIONS,
    // 16) storage
    'storage.primary_material': exports.STORAGE_PRIMARY_MATERIAL_OPTIONS,
    // 17) embellishments
    'embellishments.finish': exports.EMBELLISHMENTS_FINISH_OPTIONS,
    'embellishments.color_family': exports.COLOR_FAMILY_OPTIONS,
    // 18) blanks_bases
    'blanks_bases.blank_type': exports.BLANKS_BASES_BLANK_TYPE_OPTIONS,
    'blanks_bases.color_family': exports.COLOR_FAMILY_OPTIONS,
    // 19) tools
    'tools.primary_material': exports.TOOLS_PRIMARY_MATERIAL_OPTIONS,
};
